import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, apiFetch, getStoredToken, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  ArrowLeft, Sparkles, Music, Zap, Loader2, Copy, Check, HelpCircle, X, Wand2, Upload
} from 'lucide-react';

import MainNav from '../components/MainNav';
import { CoachEye } from '../components/Coach';
import SongsSidebar from '../components/SongsSidebar';
import StylePresetPicker from '../components/StylePresetPicker';
import MusicGptVoicePicker from '../components/MusicGptVoicePicker';

export default function CreateSong() {
  const { user, refreshUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prefillData = location.state?.prefillData;
  const leftContentRef = useRef(null);
  
  // P0 FIX: Track if auth session is verified for API calls
  const [authReady, setAuthReady] = useState(false);
  const authCheckAttempts = useRef(0);
  const [sidebarHeight, setSidebarHeight] = useState(null);
  
  // If regenerating, start at step 2 (song generation) with lyrics pre-filled
  const [step, setStep] = useState(prefillData?.isRegeneration ? 2 : 1);
  const [isGeneratingLyrics, setIsGeneratingLyrics] = useState(false);
  const [isGeneratingSong, setIsGeneratingSong] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Prompt tags from API - organized by category
  const [allTags, setAllTags] = useState([]);
  const [activeHelperCategory, setActiveHelperCategory] = useState('examples');
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  
  // P0 FIX: Verify auth session is ready before allowing API calls
  // This prevents race conditions for new users after registration/login
  useEffect(() => {
    if (authLoading) return;
    
    const verifyAuthReady = async () => {
      if (!user) {
        setAuthReady(false);
        return;
      }
      
      // For returning users, auth should be ready immediately
      if (user.credits !== undefined) {
        setAuthReady(true);
        return;
      }
      
      // For new sessions, verify with a lightweight /auth/me check
      authCheckAttempts.current++;
      if (authCheckAttempts.current <= 3) {
        try {
          await refreshUser();
          setAuthReady(true);
        } catch (e) {
          console.log('Auth verification attempt', authCheckAttempts.current, 'failed, retrying...');
          // Small delay before retry
          setTimeout(verifyAuthReady, 500);
        }
      } else {
        // After 3 attempts, trust the existing user data
        setAuthReady(true);
      }
    };
    
    verifyAuthReady();
  }, [user, authLoading, refreshUser]);
  
  // Fetch prompt tags on mount and when user changes
  useEffect(() => {
    if (!user || !authReady) return;
    
    const fetchTags = async () => {
      try {
        const { data } = await apiFetch('/prompt-tags');
        setAllTags(data);
      } catch (error) {
        console.error('Error fetching prompt tags:', error);
      }
    };
    fetchTags();
    
    // Fetch mood/genre options
    const fetchSongOptions = async () => {
      try {
        const { data } = await apiFetch('/song-options');
        if (data?.moods) setMoods([...data.moods].sort());
        if (data?.genres) setGenres([...data.genres].sort());
      } catch (error) {
        console.error('Error fetching song options:', error);
      }
    };
    fetchSongOptions();
    
    // Show toast if regenerating
    if (prefillData?.isRegeneration) {
      toast.info('Edit your lyrics and settings, then generate a new version');
    }
  }, [user, authReady, prefillData]);
  
  // Prompt helper popup state
  const [showPromptHelper, setShowPromptHelper] = useState(false);
  const [promptHelperTarget, setPromptHelperTarget] = useState(null); // 'context' or 'style' or 'lyrics'
  const [lastFocusedField, setLastFocusedField] = useState(null); // Track which field was last focused
  
  const openPromptHelper = (target) => {
    setPromptHelperTarget(target);
    setShowPromptHelper(true);
  };
  
  // Add prompt to selected list
  const addPrompt = (promptText) => {
    if (selectedPrompts.includes(promptText)) return;
    if (selectedPrompts.length >= MAX_PROMPTS) {
      toast.error(`Maximum ${MAX_PROMPTS} prompts recommended`);
      return;
    }
    setSelectedPrompts(prev => [...prev, promptText]);
  };
  
  // Remove prompt from selected list
  const removePrompt = (promptText) => {
    setSelectedPrompts(prev => prev.filter(p => p !== promptText));
  };
  
  // Select artist - just preview, don't add yet
  const selectArtist = (artist) => {
    setSelectedArtist(artist);
    // Don't auto-add - let user preview first
  };
  
  // Confirm adding artist's style prompts to the text field
  const confirmAddArtistPrompts = () => {
    if (!selectedArtist || !selectedArtist.style_prompts) return;
    
    const promptsToInsert = selectedArtist.style_prompts.map(p => `[${p}]`).join(' ');
    
    if (promptHelperTarget === 'context') {
      setAdditionalContext(prev => prev + (prev ? ' ' : '') + promptsToInsert);
    } else if (promptHelperTarget === 'style') {
      setSongPrompt(prev => prev + (prev ? ' ' : '') + promptsToInsert);
    }
    
    toast.success(`Added ${selectedArtist.tag} style preset`);
    setSelectedArtist(null);
    setShowPromptHelper(false);
  };
  
  // Handle mood/genre selection (adds as prompt)
  const handleMoodSelect = (moodValue) => {
    if (moodValue && moodValue !== 'select') {
      addPrompt(moodValue);
      // Reset dropdown
      document.getElementById('mood-inserter').value = 'select';
    }
  };
  
  const handleGenreSelect = (genreValue) => {
    if (genreValue && genreValue !== 'select') {
      addPrompt(genreValue);
      // Reset dropdown
      document.getElementById('genre-inserter').value = 'select';
    }
  };
  
  // Build the style prompt from selected prompts
  const buildStylePrompt = () => {
    return selectedPrompts.map(p => `[${p}]`).join(' ');
  };
  
  const goToStep2 = () => {
    setStep(2);
  };
  
  // Refs for cursor position tracking
  const lyricsRef = React.useRef(null);
  const contextRef = React.useRef(null);
  const styleRef = React.useRef(null);
  const [cursorPosition, setCursorPosition] = useState(0);

  const insertTag = (tag, category = null) => {
    // Use lastFocusedField if available, otherwise fall back to promptHelperTarget
    const target = lastFocusedField || promptHelperTarget;
    
    // Format tag with brackets if not already
    const formattedTag = tag.startsWith('[') ? tag : `[${tag}]`;
    
    if (target === 'context') {
      setAdditionalContext(prev => {
        if (prev.includes(formattedTag)) {
          return prev;
        }
        const ref = contextRef.current;
        if (ref) {
          const start = ref.selectionStart || prev.length;
          return prev.slice(0, start) + formattedTag + ' ' + prev.slice(start);
        }
        return prev ? `${prev} ${formattedTag}` : formattedTag;
      });
      toast.success(`Added ${formattedTag} to context`);
    } else if (target === 'style') {
      setSongPrompt(prev => {
        if (prev.includes(formattedTag)) {
          return prev;
        }
        const ref = styleRef.current;
        if (ref) {
          const start = ref.selectionStart || prev.length;
          return prev.slice(0, start) + formattedTag + ' ' + prev.slice(start);
        }
        return prev ? `${prev} ${formattedTag}` : formattedTag;
      });
    } else if (target === 'lyrics') {
      setLyrics(prev => {
        if (prev.includes(formattedTag)) {
          return prev;
        }
        const ref = lyricsRef.current;
        if (ref) {
          const start = cursorPosition || prev.length;
          const newText = prev.slice(0, start) + formattedTag + '\n' + prev.slice(start);
          setCursorPosition(start + formattedTag.length + 1);
          return newText;
        }
        return prev ? `${prev}\n${formattedTag}` : formattedTag;
      });
    } else {
      // Default to style description if no target
      setSongPrompt(prev => {
        if (prev.includes(formattedTag)) {
          return prev;
        }
        return prev ? `${prev} ${formattedTag}` : formattedTag;
      });
    }
  };
  
  // Lyric generation form
  const [theme, setTheme] = useState('');
  const [mood, setMood] = useState(prefillData?.mood || '');
  const [genre, setGenre] = useState(prefillData?.genre || '');
  const [additionalContext, setAdditionalContext] = useState('');
  
  // Song generation form
  const [lyrics, setLyrics] = useState(prefillData?.lyrics || '');
  const [lyricsFromAI, setLyricsFromAI] = useState(false);  // shows the "Digital Musicians" engine badge when true
  const [songPrompt, setSongPrompt] = useState(prefillData?.prompt || '');
  const [songTitle, setSongTitle] = useState(prefillData?.title || '');
  const [voice, setVoice] = useState(prefillData?.voice || '');
  const [instrumental, setInstrumental] = useState(false);
  const [sunoModel, setSunoModel] = useState(
    prefillData?.engine === 'musicgpt' ? 'MUSICGPT' : (prefillData?.engine === 'suno' ? 'V5' : 'MUSICGPT')
  ); // v2.9.35 — Honour engine carried over from regenerate flow; default to MusicGPT.
  const [songMood, setSongMood] = useState(prefillData?.mood || '');

  // Cover mode — when ON, user uploads an MP3 and SUNO re-records it in the
  // chosen style (preserves melody/structure). Compatible with Instrumental
  // toggle (strip vocals → instrumental version of the source).
  const [coverMode, setCoverMode] = useState(false);
  const [coverAudioUrl, setCoverAudioUrl] = useState(null);
  const [coverAudioFile, setCoverAudioFile] = useState(null);  // local File object for filename display
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverError, setCoverError] = useState('');
  // v2.9.35 — Style preset picker modal
  const [stylePresetPickerOpen, setStylePresetPickerOpen] = useState(false);
  // v2.9.36 — MusicGPT voice library picker. UUID forwarded to the
  // /MusicAI endpoint as `voice_id` so MusicGPT uses a specific artist-
  // style voice clone instead of auto-picking. Only relevant when
  // engine=MUSICGPT; the field is silently ignored for Suno.
  const [musicgptVoiceId, setMusicgptVoiceId] = useState(prefillData?.voice_id || null);

  /** Apply a chosen preset to the current form. Engine-aware:
   *  - MusicGPT → append "in the style of [Artist]" to the prompt + set
   *    the description/style with the preset tags.
   *  - Suno → just overwrite the style tags. Artist name is intentionally
   *    NOT added to the prompt because SUNO's content policy rejects it.
   */
  const applyStylePreset = (preset) => {
    if (!preset) return;
    const tags = preset.style_tags || '';
    setSongPrompt(tags);          // Advanced mode: style/description box
    setAdditionalContext(tags);   // Quick mode mirror
    if (sunoModel === 'MUSICGPT') {
      const suffix = `in the style of ${preset.artist_name}`;
      setTheme((prev) => {
        if (!prev) return suffix.charAt(0).toUpperCase() + suffix.slice(1);
        const trimmed = prev.trim().replace(/[.!?]+$/, '');
        return `${trimmed}. ${suffix}`;
      });
      toast.success(`Inspired by ${preset.artist_name} — added to your prompt.`);
    } else {
      toast.success(`Style applied: ${preset.artist_name}`);
    }
  };
  // Tracks the Whisper transcription stage separately so the Generate button
  // can stay disabled until lyrics actually land in state. Without this users
  // could click Generate the moment upload finished, before Whisper completed
  // — backend would receive empty lyrics and SUNO would produce a totally
  // unrelated song (the "Same Pulse" bug from v2.9.7).
  const [coverTranscribing, setCoverTranscribing] = useState(false);
  
  // Ref for scrolling to generate button
  const generateButtonRef = useRef(null);
  const [songGenre, setSongGenre] = useState(prefillData?.genre || '');

  // v3.0.129: render cost — pulled from /credits/costs so admin edits
  // take effect without a rebuild. Defaults to 2 credits if the fetch
  // fails (matches server default).
  const [renderCost, setRenderCost] = useState(2);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/credits/costs`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && typeof data.song_render === 'number') {
          setRenderCost(data.song_render);
        }
      } catch {
        // fall back to default 2 silently
      }
    })();
    return () => { cancelled = true; };
  }, []);
  
  // Quick/Advanced mode - persist preference (default to Quick for first-time users)
  const [isQuickMode, setIsQuickMode] = useState(() => {
    // Regenerate flow lands here with `prefillData.isRegeneration=true` —
    // force Advanced Mode so the user can access Style Builder, Style
    // Inspired By and Prompt Tags controls (Quick Mode intentionally
    // hides those). We DON'T overwrite the saved preference — the
    // save-mode useEffect below skips the first render, so this
    // override only applies to the current session.
    if (prefillData?.isRegeneration) return false;
    const saved = localStorage.getItem('songCreatorMode');
    // Default to Quick mode if no preference saved (first time user)
    if (saved === null || saved === undefined) return true;
    return saved === 'quick';
  });
  
  // Selected prompts (new prompt inserter system)
  const [selectedPrompts, setSelectedPrompts] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState(null);
  
  // Max prompts recommendation
  const MAX_PROMPTS = 6;

  // Track if initial mode has been set
  const initialModeSet = useRef(false);
  
  // Save mode preference (skip first render to avoid overwriting during hydration)
  useEffect(() => {
    if (!initialModeSet.current) {
      initialModeSet.current = true;
      return;
    }
    localStorage.setItem('songCreatorMode', isQuickMode ? 'quick' : 'advanced');
  }, [isQuickMode]);

  // Cover mode auto-advance — when the user enables Cover mode while in
  // Advanced flow, the dedicated lyrics-writing step is unnecessary because
  // Whisper auto-fills the textarea on upload. Jump straight to Step 2 so
  // the Generate button is visible. We don't auto-rewind on toggle-off so
  // users don't lose their place if they bounce the toggle.
  // NOTE: Must be placed AFTER coverMode/isQuickMode/step useState declarations
  // — putting it earlier triggers a TDZ ReferenceError that crashes the page
  // (regression in v2.9.8 fixed in v2.9.13).
  useEffect(() => {
    if (coverMode && !isQuickMode && step === 1) {
      setStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverMode, isQuickMode]);

  // Update state when prefillData changes (e.g., navigating from Regenerate)
  useEffect(() => {
    if (prefillData) {
      setLyrics(prefillData.lyrics || '');
      setSongPrompt(prefillData.prompt || '');
      setSongTitle(prefillData.title || '');
      // Reset mood and genre to None on regenerate
      setSongMood('');
      setSongGenre('');
      setMood('');
      setGenre('');
      if (prefillData.voice) {
        setVoice(prefillData.voice);
      }
      // Cover-aware regenerate: if the source song was a cover, pre-flag
      // Cover Mode + reuse the existing source MP3 so the user only has to
      // edit lyrics/style, not re-upload audio. Skip Advanced flow Step 1
      // since the lyrics textarea is already populated.
      if (prefillData.isCover && prefillData.coverAudioUrl) {
        setCoverMode(true);
        setCoverAudioUrl(prefillData.coverAudioUrl);
        // No File object to show — fake a minimal filename for display.
        try {
          const tail = prefillData.coverAudioUrl.split('/').pop() || '';
          const fname = tail.split('?')[0] || 'Source audio (reused)';
          setCoverAudioFile({ name: fname });
        } catch (_) {
          setCoverAudioFile({ name: 'Source audio (reused)' });
        }
      }
      if (prefillData.isRegeneration) {
        setStep(2);
      }
    }
  }, [prefillData]);

  // Handle Quick Start parameters from Dashboard
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const quickstart = params.get('quickstart');
    
    if (quickstart) {
      // Enable Quick Mode for quickstart
      setIsQuickMode(true);
      
      // Set presets based on quickstart type (using exact values from admin settings)
      const presets = {
        birthday: { theme: 'Birthday song', mood: 'playful', genre: 'pop' },
        love: { theme: 'Love song', mood: 'romantic', genre: 'acoustic' },
        funny: { theme: 'Funny song', mood: 'playful', genre: 'pop' },
        dog: { theme: 'Love my dog', mood: 'energetic', genre: 'country-rock' }
      };
      
      const preset = presets[quickstart];
      if (preset) {
        // Quick Mode uses songPrompt for the theme field
        setSongPrompt(preset.theme);
        setTheme(preset.theme);
        setMood(preset.mood);
        setGenre(preset.genre);
        setSongMood(preset.mood);
        setSongGenre(preset.genre);
        // Skip to voice selection (step 2) after a brief delay to let state update
        setTimeout(() => setStep(2), 100);
      }
    }
  }, [location.search]);

  // Mood and genre options from API
  const [moods, setMoods] = useState(['agressive', 'dark', 'energetic', 'hopeful', 'melancholic', 'nostalgic', 'peaceful', 'romantic', 'upbeat']);
  const [genres, setGenres] = useState(['acoustic', 'classical', 'country', 'country-rock', 'electronic', 'gospel', 'hip-hop', 'indie', 'jazz', 'metal', 'pop', 'r&b', 'rock', 'smooth jazz']);
  const voices = [
    { id: '', label: 'None', emoji: '🚫' },
    { id: 'female', label: 'Female', emoji: '👩' },
    { id: 'male', label: 'Male', emoji: '👨' },
    { id: 'duet', label: 'Duet', emoji: '👫' }
  ];

  const handleGenerateLyrics = async () => {
    if (!theme.trim()) {
      toast.error('Please enter a theme for your lyrics');
      return;
    }

    // P0 FIX: Check if auth session is ready before making API call
    if (!authReady) {
      toast.error('Please wait, verifying your session...');
      // Try to refresh user and retry
      await refreshUser();
      setAuthReady(true);
      return;
    }

    if ((user?.credits || 0) < 5) {
      toast.error('Insufficient credits. You need 5 credits for lyric generation.');
      return;
    }

    setIsGeneratingLyrics(true);
    
    try {
      // Start async lyrics generation
      const { data } = await apiFetch('/lyrics/generate', {
        method: 'POST',
        body: JSON.stringify({
          theme,
          mood,
          genre,
          additional_context: additionalContext || null
        })
      });

      if (!data.lyrics_id) {
        throw new Error('Failed to start lyrics generation');
      }

      // Poll for completion
      const lyricsId = data.lyrics_id;
      let attempts = 0;
      const maxAttempts = 60; // 2 minutes max (2 seconds per poll)
      
      const pollStatus = async () => {
        attempts++;
        try {
          const { data: statusData } = await apiFetch(`/lyrics/${lyricsId}`);
          
          if (statusData.status === 'completed') {
            setLyrics(statusData.lyrics);
            setLyricsFromAI(true);
            setSongMood(mood);
            setSongGenre(genre);
            // songPrompt already has all prompts from Step 1
            await refreshUser();
            toast.success(`🎵 Our digital musicians have finished your lyrics — ${statusData.credits_used} credits used`);
            setStep(2);
            setIsGeneratingLyrics(false);
            return;
          } else if (statusData.status === 'failed') {
            toast.error(statusData.error || 'Lyrics generation failed');
            setIsGeneratingLyrics(false);
            return;
          } else if (attempts < maxAttempts) {
            // Still processing, poll again
            setTimeout(pollStatus, 2000);
          } else {
            toast.error('Lyrics generation timed out. Please try again.');
            setIsGeneratingLyrics(false);
          }
        } catch (pollError) {
          console.error('Polling error:', pollError);
          toast.error('Error checking lyrics status');
          setIsGeneratingLyrics(false);
        }
      };
      
      // Start polling
      setTimeout(pollStatus, 2000);
      
    } catch (error) {
      console.error('Lyric generation error:', error);
      const message = error.response?.data?.detail || error.message || 'Failed to generate lyrics';
      toast.error(message);
      setIsGeneratingLyrics(false);
    }
  };

  const handleGenerateSong = async () => {
    console.log('handleGenerateSong called - user:', user?.email, 'authReady:', authReady);

    // Cover mode: only requirement is the uploaded source MP3 — text fields
    // are optional. SUNO analyzes the source and returns lyrics + style on
    // delivery, which we auto-save to the song record.
    if (coverMode) {
      if (!coverAudioUrl) {
        toast.error('Please upload your source MP3 first.');
        return;
      }
      // v3.0.131 — MusicGPT's /Cover endpoint requires voice_id.
      // Surface the requirement client-side so users don't hit a
      // 400 from the backend.
      if (sunoModel === 'MUSICGPT' && !musicgptVoiceId) {
        toast.error('MusicGPT covers need a voice — open the Voice picker to choose one.');
        return;
      }
    } else if (!songPrompt.trim() && !theme.trim() && !lyrics.trim()) {
      toast.error('Please provide a prompt or lyrics for your song');
      return;
    }

    // P0 FIX: Check if auth session is ready before making API call
    if (!user) {
      console.log('No user found - redirecting to auth');
      toast.error('Please log in to generate songs');
      navigate('/auth');
      return;
    }
    
    if (!authReady) {
      console.log('Auth not ready - verifying session');
      toast.info('Verifying your session...');
      try {
        await refreshUser();
        setAuthReady(true);
      } catch (e) {
        console.log('Session verification failed:', e);
        toast.error('Session expired. Please log in again.');
        navigate('/auth');
        return;
      }
    }

    console.log('Auth verified, proceeding with song generation');
    // Song generation is FREE - no credit check needed
    // Credits are only charged when unlocking the song

    setIsGeneratingSong(true);
    try {
      // Build enhanced prompt - use selected prompts in Advanced mode, or traditional mood/genre in Quick mode
      let fullPrompt;
      // In Advanced Mode, use mood/genre from Step 1. In Quick Mode, use songMood/songGenre
      const effectiveMood = isQuickMode ? songMood : mood;
      const effectiveGenre = isQuickMode ? songGenre : genre;
      
      if (isQuickMode) {
        // Quick mode: theme is the song description, songPrompt has style tags
        const themePart = theme.trim();
        const stylePart = songPrompt.trim();
        fullPrompt = `${effectiveGenre} song, ${effectiveMood} mood. ${themePart}${stylePart ? '. ' + stylePart : ''}`.trim();
      } else {
        // Advanced mode: use selected prompts + mood/genre from Step 1 (if set)
        const promptPart = selectedPrompts.length > 0 ? buildStylePrompt() : '';
        const descPart = songPrompt.trim();
        // Build mood/genre part only if they're set
        const moodGenreParts = [];
        if (effectiveGenre) moodGenreParts.push(`${effectiveGenre} song`);
        if (effectiveMood) moodGenreParts.push(`${effectiveMood} mood`);
        const moodGenrePart = moodGenreParts.join(', ');
        fullPrompt = [moodGenrePart, promptPart, descPart].filter(Boolean).join('. ').trim();
      }
      
      console.log('Generating song with:', { fullPrompt, description: songPrompt, lyrics: !!lyrics, title: songTitle, voice, instrumental, mood: effectiveMood, genre: effectiveGenre, selectedPrompts, sunoModel, isCover: coverMode });

      const { data } = await apiFetch('/songs/generate', {
        method: 'POST',
        body: JSON.stringify({
          // For Cover mode, send a permissive style hint when user typed nothing.
          // Backend has its own better fallback ("Match the original arrangement…")
          // but we still need a non-empty string here for the pydantic model.
          prompt: coverMode && !songPrompt.trim() && !theme.trim() ? '' : fullPrompt,
          description: songPrompt,  // Store raw user description for regeneration
          lyrics: instrumental ? null : (lyrics || null),
          title: songTitle || null,
          voice: instrumental ? null : voice,
          instrumental: instrumental,
          mood: effectiveMood || null,
          genre: effectiveGenre || null,
          selected_prompts: selectedPrompts,  // Store for regeneration
          // v2.9.34 — Unified engine selector. The dropdown still labelled "SUNO"
          // can now pick a MusicGPT track too. We strip the "MUSICGPT" sentinel
          // off the suno_model field before sending — backend reads `engine`
          // first and only consults suno_model when engine == "suno".
          engine: sunoModel === 'MUSICGPT' ? 'musicgpt' : 'suno',
          suno_model: sunoModel === 'MUSICGPT' ? 'V5' : sunoModel,
          // v2.9.36 — MusicGPT-only voice clone selection. Backend
          // ignores this when engine != "musicgpt".
          voice_id: sunoModel === 'MUSICGPT' ? (musicgptVoiceId || null) : null,
          cover_audio_url: coverMode ? coverAudioUrl : null,  // Cover mode source MP3
          // Tracks the manual-regenerate parent so the new song's detail page
          // can show a "View original" link back to it.
          original_song_id: prefillData?.originalSongId || null,
        })
      });

      console.log('Song generation response:', data);
      
      if (!data.song_id) {
        throw new Error('No song ID received from server');
      }

      await refreshUser();
      const etaCopy = sunoModel === 'MUSICGPT' ? '1-2' : '2-4';
      toast.success(
        `Song generation started! Usually takes ${etaCopy} minutes — feel free to browse the site, we'll notify you when it's ready.`,
        { duration: 7000 }
      );
      navigate(`/song/${data.song_id}`);
    } catch (error) {
      console.error('Song generation error:', error);
      const message = error.response?.data?.detail || error.message || 'Failed to generate song';
      toast.error(message);
    } finally {
      setIsGeneratingSong(false);
    }
  };

  const handleCopyLyrics = () => {
    navigator.clipboard.writeText(lyrics);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Lyrics copied to clipboard');
  };

  // Measure left content height for dynamic sidebar sizing
  useEffect(() => {
    const measureHeight = () => {
      if (leftContentRef.current) {
        setSidebarHeight(leftContentRef.current.offsetHeight);
      }
    };
    
    measureHeight();
    window.addEventListener('resize', measureHeight);
    return () => window.removeEventListener('resize', measureHeight);
  }, [step, isQuickMode, lyrics]);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <MainNav />

      {/* Main Content with Sidebar */}
      <main className="pt-24 pb-12 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto flex gap-8">
          {/* Main Form Area */}
          <div ref={leftContentRef} className="flex-1 max-w-2xl">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">
                <span className="mr-2">🎵</span>Let's make your next song
              </h1>
              <p className="text-zinc-400">
                {isQuickMode ? "Just describe it—we'll handle the rest" : 'Our digital musicians craft your lyrics with care — then turn them into a full song'}
              </p>
            </div>

        {/* Progress Steps - Only in Advanced Mode */}
        {!isQuickMode && (
          <div className="flex items-center gap-4 mb-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-zinc-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary text-white' : 'bg-white/10'}`}>
                1
              </div>
              <span className="text-sm font-medium hidden sm:block">Write Lyrics</span>
            </div>
            <div className="flex-1 h-px bg-white/10"></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-zinc-500'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary text-white' : 'bg-white/10'}`}>
                2
              </div>
              <span className="text-sm font-medium hidden sm:block">Create Song</span>
            </div>
          </div>
        )}

        {/* Mode Toggle & Instrumental */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            {/* Quick/Advanced Mode Toggle */}
            <div className="flex-1 min-w-[220px]">
              <div className="flex rounded-lg overflow-hidden border border-white/10">
                <button
                  onClick={() => {
                    // Preserve work when switching Advanced → Quick:
                    // copy theme/mood/genre that were typed in Advanced
                    // into the matching Quick fields so the user
                    // doesn't lose context. Only copy if the source
                    // field has content AND the destination is empty
                    // (don't blow away an existing Quick entry).
                    if (!isQuickMode) {
                      if (theme && !songPrompt) setSongPrompt(theme);
                      if (mood && !songMood) setSongMood(mood);
                      if (genre && !songGenre) setSongGenre(genre);
                    }
                    setIsQuickMode(true);
                  }}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-all ${
                    isQuickMode 
                      ? 'bg-primary text-white' 
                      : 'bg-black/20 text-zinc-400 hover:text-white'
                  }`}
                  data-testid="quick-mode-btn"
                >
                  ⚡ Quick Mode
                </button>
                <button
                  onClick={() => {
                    // Preserve work when switching Quick → Advanced:
                    // copy songPrompt/songMood/songGenre into the
                    // Advanced fields so the user can continue with
                    // their thought instead of re-typing.
                    if (isQuickMode) {
                      if (songPrompt && !theme) setTheme(songPrompt);
                      if (songMood && !mood) setMood(songMood);
                      if (songGenre && !genre) setGenre(songGenre);
                    }
                    setIsQuickMode(false);
                  }}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-all ${
                    !isQuickMode 
                      ? 'bg-primary text-white' 
                      : 'bg-black/20 text-zinc-400 hover:text-white'
                  }`}
                  data-testid="advanced-mode-btn"
                >
                  🎛️ Advanced Mode
                </button>
              </div>
              <p className="text-xs text-zinc-500 mt-2 text-center">
                {isQuickMode ? 'Simple: just describe and generate' : 'Want more control? Customize your lyrics, style, and sound'}
              </p>
            </div>
            
            {/* Instrumental Toggle - Available in both modes */}
            <div className="flex-1 min-w-[220px]">
              <button
                type="button"
                onClick={() => setInstrumental(!instrumental)}
                className={`w-full h-[46px] rounded-lg flex items-center justify-between px-4 transition-all ${
                  instrumental 
                    ? 'bg-primary/20 border-2 border-primary' 
                    : 'bg-black/20 border-2 border-white/10 hover:border-white/20'
                }`}
                data-testid="instrumental-toggle"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">🎹</span>
                  <span className="font-medium text-white text-sm">Instrumental Only</span>
                </div>
                <div className={`w-10 h-6 rounded-full transition-all ${instrumental ? 'bg-primary' : 'bg-zinc-600'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white mt-0.5 transition-all ${instrumental ? 'translate-x-4' : 'translate-x-0.5'}`}></div>
                </div>
              </button>
              <p className="text-xs text-zinc-500 mt-2 text-center">
                {instrumental ? 'No vocals - music only' : 'Includes vocals'}
              </p>
            </div>

            {/* Cover Mode + Engine + Voice — all hidden in Quick Mode
                to keep the UI clean. Advanced Mode reveals them for
                power users who want to choose engine, do covers, etc. */}
            {!isQuickMode && (
              <>
            {/* Cover Mode Toggle — when ON, user uploads an MP3 and SUNO
                re-records it in the chosen style (preserving melody/structure).
                Compatible with Instrumental toggle (strip vocals → instrumental
                cover). Source URL is uploaded via /api/songs/cover/upload. */}
            <div className="flex-1 min-w-[220px]">
              <button
                type="button"
                onClick={() => {
                  const next = !coverMode;
                  // Cover Mode requires Suno (MusicGPT doesn't support
                  // re-style-from-MP3 yet). Auto-switch engine on enable so
                  // the user doesn't hit a confusing 400 after submission.
                  if (next && sunoModel === 'MUSICGPT') {
                    setSunoModel('V5');
                    toast.info('Cover Song requires Suno — engine switched automatically.');
                  }
                  setCoverMode(next);
                  if (!next) {
                    // Toggling OFF clears any uploaded source so we don't
                    // accidentally submit a stale cover URL on a regular song.
                    setCoverAudioUrl(null);
                    setCoverAudioFile(null);
                    setCoverError('');
                  }
                }}
                className={`w-full h-[46px] rounded-lg flex items-center justify-between px-4 transition-all ${
                  coverMode 
                    ? 'bg-amber-500/20 border-2 border-amber-500' 
                    : 'bg-black/20 border-2 border-white/10 hover:border-white/20'
                }`}
                data-testid="cover-mode-toggle"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">🎤</span>
                  <span className="font-medium text-white text-sm">Cover Song</span>
                </div>
                <div className={`w-10 h-6 rounded-full transition-all ${coverMode ? 'bg-amber-500' : 'bg-zinc-600'}`}>
                  <div className={`w-5 h-5 rounded-full bg-white mt-0.5 transition-all ${coverMode ? 'translate-x-4' : 'translate-x-0.5'}`}></div>
                </div>
              </button>
              <p className="text-xs text-zinc-500 mt-2 text-center">
                {coverMode ? 'Upload MP3 → reimagine in new style' : 'Generate from scratch'}
              </p>
            </div>

            {/* SUNO Model Selection */}
            <div className="flex-1 min-w-[220px]">
              <div className="w-full h-[46px] rounded-lg bg-black/20 border-2 border-white/10 flex items-center px-4">
                <div className="flex items-center gap-2 mr-3">
                  <span className="text-lg">🎛️</span>
                  <span className="font-medium text-white text-sm">Engine:</span>
                </div>
                <select
                  value={sunoModel}
                  onChange={(e) => {
                    const next = e.target.value;
                    // v3.0.131 — MusicGPT now supports Cover Songs, no need
                    // to disable Cover Mode when switching to it. The
                    // backend validates that a voice_id is present for
                    // MusicGPT covers (their /Cover API requires it).
                    // Voice picker only applies to MusicGPT — wipe stale UUID
                    // so it doesn't get accidentally re-sent for a Suno gen.
                    if (next !== 'MUSICGPT' && musicgptVoiceId) {
                      setMusicgptVoiceId(null);
                    }
                    setSunoModel(next);
                  }}
                  className="flex-1 bg-transparent text-white text-sm border-none outline-none cursor-pointer"
                  data-testid="suno-model-select"
                >
                  <option value="MUSICGPT" className="bg-zinc-800">MusicGPT</option>
                  <option value="V5" className="bg-zinc-800">Suno</option>
                </select>
              </div>
            </div>

            {/* v2.9.36 — MusicGPT voice picker (engine-aware, hidden for Suno) */}
            {sunoModel === 'MUSICGPT' && (
              <div className="flex-1 min-w-[220px]">
                <MusicGptVoicePicker
                  value={musicgptVoiceId}
                  onChange={(id) => setMusicgptVoiceId(id)}
                />
              </div>
            )}
              </>
            )}
          </div>
        </div>

        {/* Cover Mode Upload Panel — only shown when toggle is ON. Lets the
            user attach the source MP3 and shows a soft copyright reminder. */}
        {coverMode && (
          <div className="glass-card p-5 border-amber-500/30" data-testid="cover-upload-panel">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-base font-semibold text-white flex items-center gap-2">
                  <span>🎤</span> Source audio for your cover
                </h3>
                <p className="text-xs text-zinc-400 mt-1 max-w-2xl">
                  Just upload an MP3, WAV or M4A (max 8 minutes). Our lyricists
                  will transcribe the lyrics automatically so SUNO sings your
                  actual words. Review the transcribed lyrics below — edit
                  anything they got wrong — then click Generate. Want a
                  specific style direction? Add a hint in the description
                  (e.g. "80s synthwave", "Jazz quartet"); leave blank to
                  clean up the original arrangement. Personal-use covers are
                  typically fine; if you plan to distribute or monetize,
                  make sure you have the right mechanical license.
                </p>
              </div>
            </div>

            {coverAudioUrl ? (
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-lg">✅</span>
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate" title={coverAudioFile?.name}>
                      {coverAudioFile?.name || 'Source audio uploaded'}
                    </p>
                    <p className="text-[11px] text-emerald-300">
                      Ready — describe the new style below and hit Generate.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setCoverAudioUrl(null);
                    setCoverAudioFile(null);
                    setCoverError('');
                  }}
                  className="text-xs px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  data-testid="cover-clear-btn"
                >
                  Replace
                </button>
              </div>
            ) : (
              <label className={`flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                coverUploading
                  ? 'border-amber-500/50 bg-amber-500/5'
                  : 'border-zinc-700 hover:border-amber-500/50 hover:bg-amber-500/5'
              }`} data-testid="cover-upload-dropzone">
                <input
                  type="file"
                  accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,audio/*"
                  className="hidden"
                  disabled={coverUploading}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setCoverError('');
                    setCoverUploading(true);
                    setCoverAudioFile(file);
                    try {
                      // Step 1 — upload to HP (fast, returns the public URL)
                      const fd = new FormData();
                      fd.append('file', file);
                      const token = getStoredToken();
                      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/songs/cover/upload`, {
                        method: 'POST',
                        body: fd,
                        credentials: 'include',
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                      });
                      const data = await r.json().catch(() => ({}));
                      if (!r.ok) throw new Error(data.detail || `Upload failed (${r.status})`);
                      setCoverAudioUrl(data.url);
                      const dur = data.duration_seconds;
                      const durMsg = dur ? ` (${(dur/60).toFixed(1)} min)` : '';

                      // Step 2 — kick off background Whisper transcription
                      // and poll for completion. Splits the work into many
                      // short requests so we never trip Cloudflare's 100s
                      // proxy timeout (longer source MP3s used to 504 here).
                      toast.success(`Source uploaded${durMsg}. Transcribing lyrics…`);
                      setCoverTranscribing(true);
                      try {
                        // 2a) Start the background job
                        const t = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/songs/cover/transcribe`, {
                          method: 'POST',
                          credentials: 'include',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { Authorization: `Bearer ${token}` } : {}),
                          },
                          body: JSON.stringify({ url: data.url }),
                        });
                        const td = await t.json().catch(() => ({}));
                        if (!t.ok || !td.job_id) {
                          throw new Error(td.detail || 'Could not start transcription');
                        }

                        // 2b) Poll the job until status is complete or failed.
                        // Up to ~5 minutes total (100 polls * 3 s), generous
                        // enough for any MP3 within the 25 MB Whisper limit.
                        const jobId = td.job_id;
                        let finalLyrics = null;
                        let pollErr = null;
                        for (let i = 0; i < 100; i++) {
                          await new Promise((res) => setTimeout(res, 3000));
                          try {
                            const sr = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/songs/cover/transcribe/${jobId}`, {
                              method: 'GET',
                              credentials: 'include',
                              headers: token ? { Authorization: `Bearer ${token}` } : {},
                            });
                            if (!sr.ok) continue;
                            const sd = await sr.json().catch(() => ({}));
                            if (sd.status === 'complete') {
                              finalLyrics = sd.lyrics || null;
                              break;
                            }
                            if (sd.status === 'failed') {
                              pollErr = sd.error || 'Transcription failed';
                              break;
                            }
                            // queued / transcribing / formatting — keep polling
                          } catch (_) {
                            // transient network blip — keep polling
                          }
                        }

                        if (finalLyrics && !lyrics?.trim()) {
                          setLyrics(finalLyrics);
                          toast.success('Lyrics transcribed — review them below before generating.', { duration: 6000 });
                        } else if (pollErr) {
                          toast.info('Transcription unavailable — type your lyrics below or leave blank for SUNO to write fresh ones.');
                        } else if (!finalLyrics) {
                          // Timed out without complete — likely empty audio
                          toast.info('Transcription took too long — type your lyrics below or leave blank for SUNO to write fresh ones.');
                        }
                      } catch (transcribeErr) {
                        // Non-fatal — user can still proceed
                        toast.info('Transcription failed — type lyrics below or leave blank for SUNO to write fresh ones.');
                      } finally {
                        setCoverTranscribing(false);
                      }
                    } catch (err) {
                      setCoverError(err.message || 'Upload failed');
                      setCoverAudioFile(null);
                      toast.error(err.message || 'Upload failed');
                    } finally {
                      setCoverUploading(false);
                    }
                  }}
                />
                <Upload className={`h-7 w-7 ${coverUploading ? 'text-amber-400 animate-pulse' : 'text-zinc-500'}`} />
                <p className="text-sm font-medium text-white">
                  {coverUploading ? 'Uploading…' : 'Click to upload your audio'}
                </p>
                <p className="text-[11px] text-zinc-500">MP3, WAV, M4A, AAC, OGG, FLAC · up to 50 MB · 8 min max</p>
              </label>
            )}

            {coverError && (
              <p className="text-xs text-red-400 mt-2" data-testid="cover-upload-error">
                {coverError}
              </p>
            )}
          </div>
        )}

        {/* QUICK MODE */}
        {isQuickMode && (
          <div className="glass-card p-6" data-testid="quick-mode-form">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Zap className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">
                  <span className="mr-1">⚡</span>Quick Song Creator
                </h2>
                <p className="text-sm text-zinc-400">Start with a word or idea—we'll turn it into a song</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Theme/Idea Input */}
              <div>
                <Label htmlFor="quickTheme" className="text-white mb-2 flex items-center gap-2">
                  <span>{instrumental ? 'Describe your music' : "What's your song about?"} *</span>
                  <CoachEye targetId="create.about" label="What's your song about?" />
                </Label>
                <textarea
                  id="quickTheme"
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  placeholder={instrumental 
                    ? "e.g., peaceful piano melody, upbeat jazz, epic orchestral..." 
                    : "e.g., falling in love, summer memories, overcoming challenges..."
                  }
                  className="input-dark w-full min-h-[42px] resize-y py-2 px-3"
                  data-testid="quick-theme-input"
                  rows={2}
                />
                <p className="text-xs text-zinc-500 mt-1">
                  {instrumental ? 'Describe the style, instruments, or feeling' : 'Even one word works 👍 Drag corner to expand.'}
                </p>
              </div>

              {/* Mood & Style - insert as tags */}
              <div>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <p className="text-sm text-zinc-400 flex items-center gap-2">
                    <span>Choose how it should feel and sound</span>
                    <CoachEye targetId="create.mood_genre" label="Mood & Genre" />
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setStylePresetPickerOpen(true)}
                      className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                      data-testid="open-style-preset-picker-quick"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Style Inspired By…
                    </button>
                    <CoachEye targetId="create.style_inspired_by" label="Style Inspired By" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="quickMood" className="text-white mb-2 block">Mood (Optional)</Label>
                    <select
                      id="quickMood"
                      value={songMood}
                      onChange={(e) => {
                        const newMood = e.target.value;
                        // Remove old mood tag if exists
                        if (songMood) {
                          setSongPrompt(prev => prev.replace(`[${songMood}]`, '').replace(/\s+/g, ' ').trim());
                        }
                        setSongMood(newMood);
                        // Insert new mood tag
                        if (newMood) {
                          const newTag = `[${newMood}]`;
                          setSongPrompt(prev => {
                            if (prev.includes(newTag)) {
                              return prev;
                            }
                            return prev ? `${newTag} ${prev}` : newTag;
                          });
                        }
                      }}
                      className="custom-select w-full"
                      data-testid="quick-mood-select"
                    >
                      <option value="">-- None --</option>
                      {moods.map((m) => (
                        <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="quickGenre" className="text-white mb-2 block">Style (Optional)</Label>
                    <select
                      id="quickGenre"
                      value={songGenre}
                      onChange={(e) => {
                        const newGenre = e.target.value;
                        // Remove old genre tag if exists
                        if (songGenre) {
                          setSongPrompt(prev => prev.replace(`[${songGenre}]`, '').replace(/\s+/g, ' ').trim());
                        }
                        setSongGenre(newGenre);
                        // Insert new genre tag
                        if (newGenre) {
                          const newTag = `[${newGenre}]`;
                          setSongPrompt(prev => {
                            if (prev.includes(newTag)) {
                              return prev;
                            }
                            return prev ? `${newTag} ${prev}` : newTag;
                          });
                        }
                      }}
                      className="custom-select w-full"
                      data-testid="quick-genre-select"
                    >
                      <option value="">-- None --</option>
                      {genres.map((g) => (
                        <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Additional Style Tags + Style Inspired By + Prompt Tags
                  removed from Quick Mode (kept in Advanced Mode below)
                  to keep the Quick flow minimal — user just picks
                  mood/genre and types a theme. */}

              {/* Voice Selection — explicit None / Female / Male / Duet.
                  Always visible in both Quick and Advanced modes (user
                  request). When instrumental is on, the buttons are
                  visually disabled but stay in place so the layout
                  doesn't shift. */}
              <div data-testid="quick-voice-section">
                <Label className="text-white mb-2 block">Voice</Label>
                <div className="grid grid-cols-4 gap-2">
                  {voices.map((v) => {
                    const isActive = voice === v.id && !instrumental;
                    return (
                      <button
                        key={v.id || 'none'}
                        type="button"
                        disabled={instrumental}
                        onClick={() => setVoice(v.id)}
                        className={`p-3 rounded-lg text-center transition-all ${
                          isActive
                            ? 'bg-primary/20 border-2 border-primary'
                            : 'bg-black/20 border-2 border-white/10 hover:border-white/20'
                        } ${instrumental ? 'opacity-40 cursor-not-allowed' : ''}`}
                        data-testid={`quick-voice-${v.id || 'none'}`}
                      >
                        <div className="text-xl mb-1">{v.emoji}</div>
                        <div className="font-medium text-white text-sm">{v.label}</div>
                      </button>
                    );
                  })}
                </div>
                {instrumental && (
                  <p className="text-xs text-zinc-500 mt-2">
                    🎹 Instrumental mode — voice selection disabled
                  </p>
                )}
              </div>

              {/* Generate Button */}
              <div ref={generateButtonRef} className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                <p className="text-sm text-green-400">
                  <strong>{renderCost} credit{renderCost === 1 ? '' : 's'} per render</strong>
                  &nbsp;— you get 2 song variations. Unlock only the ones you love.
                </p>
              </div>

              <Button
                onClick={handleGenerateSong}
                disabled={isGeneratingSong || coverTranscribing || (coverMode ? !coverAudioUrl : !songPrompt.trim())}
                className="btn-primary w-full py-6 text-lg"
                data-testid="quick-generate-btn"
              >
                {coverTranscribing ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Transcribing lyrics…
                  </>
                ) : isGeneratingSong ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Creating Your Song...
                  </>
                ) : (
                  <>
                    🎵 Create My {instrumental ? 'Instrumental' : 'Song'}
                    <span className="ml-2 text-sm opacity-80">
                      • {renderCost} credit{renderCost === 1 ? '' : 's'}
                    </span>
                  </>
                )}
              </Button>

              {/* Quick Mode caption — tells first-time users what engine
                  and voice are being used so they understand exactly
                  what's happening without re-cluttering the UI. */}
              <p className="text-xs text-zinc-500 text-center -mt-2" data-testid="quick-mode-engine-caption">
                Powered by <span className="text-zinc-300 font-medium">MusicGPT</span>
                {!instrumental && (
                  <> with <span className="text-zinc-300 font-medium">auto-voice</span></>
                )}
                {' · '}
                <button
                  type="button"
                  onClick={() => {
                    // Same preservation logic as the Advanced Mode toggle
                    // — copy Quick fields into Advanced fields so the
                    // user doesn't lose what they've already typed.
                    if (songPrompt && !theme) setTheme(songPrompt);
                    if (songMood && !mood) setMood(songMood);
                    if (songGenre && !genre) setGenre(songGenre);
                    setIsQuickMode(false);
                  }}
                  className="text-primary hover:text-primary/80 underline-offset-2 hover:underline"
                  data-testid="quick-mode-customize-link"
                >
                  Customize
                </button>
              </p>
            </div>
          </div>
        )}

        {/* ADVANCED MODE - Step 1: Lyric Generation */}
        {!isQuickMode && step === 1 && (
          <div className="glass-card p-6" data-testid="step-1-lyrics">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">✍️ Write Your Lyrics</h2>
                <p className="text-sm text-zinc-400">5 credits</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <Label htmlFor="theme" className="text-white mb-2 flex items-center gap-2">
                  <span>What's your song about? *</span>
                  <CoachEye targetId="create.about" label="What's your song about?" />
                </Label>
                <textarea
                  id="theme"
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  placeholder="e.g., Finding love after heartbreak, Summer road trip adventure"
                  className="input-dark w-full min-h-[42px] resize-y py-2 px-3"
                  data-testid="theme-input"
                  rows={2}
                />
                <p className="text-xs text-zinc-500 mt-1">Drag corner to expand</p>
                <p className="text-[11px] text-amber-400/90 mt-1.5 leading-snug flex items-start gap-1.5 flex-wrap" data-testid="theme-artist-warning">
                  {sunoModel === 'MUSICGPT' ? (
                    <span>
                      <span className="font-semibold">Tip:</span> specific artist or band styles can be selected from
                      {' '}<span className="text-amber-300 font-medium">Style Inspired by…</span>, or you can access
                      {' '}<span className="text-amber-300 font-medium">Prompt Tags</span> for even more control over
                      instruments, genres and moods.
                    </span>
                  ) : (
                    <span>
                      <span className="font-semibold">Heads up:</span> don&apos;t use real artist names here.
                      Pick from <span className="text-amber-300 font-medium">Style Inspired by…</span> instead, or use
                      {' '}<span className="text-amber-300 font-medium">Prompt Tags</span> for finer control.
                    </span>
                  )}
                  <CoachEye targetId="create.mood_genre" label="Mood & Genre" />
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mood" className="text-white mb-2 block">Mood (Optional)</Label>
                  <select
                    id="mood"
                    value={mood}
                    onChange={(e) => {
                      const newMood = e.target.value;
                      // Remove old mood tag from songPrompt if exists
                      if (mood) {
                        setSongPrompt(prev => prev.replace(`[${mood}]`, '').replace(/\s+/g, ' ').trim());
                      }
                      setMood(newMood);
                      // Insert new mood tag into songPrompt
                      if (newMood) {
                        const newTag = `[${newMood}]`;
                        setSongPrompt(prev => {
                          if (prev.includes(newTag)) {
                            return prev;
                          }
                          return prev ? `${newTag} ${prev}` : newTag;
                        });
                      }
                    }}
                    className="custom-select w-full"
                    data-testid="mood-select"
                  >
                    <option value="">-- None --</option>
                    {moods.map((m) => (
                      <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="genre" className="text-white mb-2 block">Genre (Optional)</Label>
                  <select
                    id="genre"
                    value={genre}
                    onChange={(e) => {
                      const newGenre = e.target.value;
                      // Remove old genre tag from songPrompt if exists
                      if (genre) {
                        setSongPrompt(prev => prev.replace(`[${genre}]`, '').replace(/\s+/g, ' ').trim());
                      }
                      setGenre(newGenre);
                      // Insert new genre tag into songPrompt
                      if (newGenre) {
                        const newTag = `[${newGenre}]`;
                        setSongPrompt(prev => {
                          if (prev.includes(newTag)) {
                            return prev;
                          }
                          return prev ? `${newTag} ${prev}` : newTag;
                        });
                      }
                    }}
                    className="custom-select w-full"
                    data-testid="genre-select"
                  >
                    <option value="">-- None --</option>
                    {genres.map((g) => (
                      <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Style Description / Additional Context - same field as Step 2 */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="context" className="text-white flex items-center gap-2">
                    <span>Style Builder</span>
                    <CoachEye targetId="create.style_builder" label="Style Builder" />
                  </Label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setStylePresetPickerOpen(true)}
                      className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300 transition-colors"
                      data-testid="open-style-preset-picker-style"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Style Inspired By…
                    </button>
                    <CoachEye targetId="create.style_inspired_by" label="Style Inspired By" />
                    <button
                      type="button"
                      onClick={() => openPromptHelper('style')}
                      className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                      data-testid="context-helper-btn"
                    >
                      <HelpCircle className="h-3.5 w-3.5" />
                      Prompt Tags
                    </button>
                    <CoachEye targetId="create.suno_prompt_tags_ref" label="SUNO Prompt Tags Reference" />
                  </div>
                </div>
                <textarea
                  ref={styleRef}
                  id="context"
                  value={songPrompt}
                  onChange={(e) => setSongPrompt(e.target.value)}
                  onFocus={() => setLastFocusedField('style')}
                  placeholder="Your mood & genre tags will appear here. Add more with Prompt Tags..."
                  className="textarea-dark w-full h-24 font-mono text-sm"
                  data-testid="context-input"
                />
                <p className="text-xs text-zinc-500 mt-1">These prompts carry through to Song Generation</p>
                <p className="text-[11px] text-amber-400/90 mt-1.5 leading-snug" data-testid="style-artist-warning">
                  {sunoModel === 'MUSICGPT' ? (
                    <>
                      <span className="font-semibold">Tip:</span> specific artist or band styles can be selected from
                      our <span className="text-primary font-medium">Prompt Tags</span> presets, or you can request
                      &ldquo;in the style of [Artist/Band Name]&rdquo; directly.
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">Heads up:</span> don&apos;t type real artist names here.
                      Use the <span className="text-primary font-medium">Prompt Tags</span> button above for genre/style presets.
                    </>
                  )}
                </p>
              </div>

              <div className="flex gap-4 items-stretch">
                <div className="flex-1 flex flex-col gap-1.5">
                  <Button
                    onClick={handleGenerateLyrics}
                    disabled={isGeneratingLyrics || !theme.trim()}
                    className="btn-primary w-full"
                    data-testid="generate-lyrics-btn"
                  >
                    {isGeneratingLyrics ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Our digital musicians are writing your lyrics...
                      </>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 mr-1" />
                        ✨ Pro Lyrics
                        <CoachEye targetId="create.pro_lyrics" label="Pro Lyrics" />
                      </span>
                    )}
                  </Button>
                  {!isGeneratingLyrics && (
                    <div className="flex items-center justify-center gap-1.5 text-[11px] text-zinc-400">
                      <span className="px-1.5 py-0.5 bg-purple-500/15 text-purple-300 rounded font-medium">Digital Song Writer</span>
                      <span>·</span>
                      <span>5 credits</span>
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="ml-0.5 text-zinc-500 hover:text-zinc-300 transition-colors"
                              data-testid="pro-lyrics-info-btn"
                              aria-label="What are Pro Lyrics?"
                            >
                              <HelpCircle className="h-3.5 w-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-xs bg-zinc-900 text-zinc-100 border border-white/10 px-3 py-2.5 text-xs leading-relaxed"
                          >
                            <p className="font-semibold mb-1 text-purple-300">Premium Digital Musician Lyrics</p>
                            <p>
                              Pro Lyrics are hand-crafted by our digital musicians — a dedicated
                              lyric team that produces polished, structured songs with verse, chorus
                              and bridge, richer metaphors, and clearer emotional arcs than the
                              auto-lyrics your music engine generates from a basic prompt.
                            </p>
                            <p className="mt-2 text-zinc-400">
                              Used by our songwriters for their best work. You can edit before sending to SUNO.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  )}
                </div>
                <Button
                  onClick={goToStep2}
                  variant="outline"
                  className="btn-secondary"
                  data-testid="skip-lyrics-btn"
                >
                  Skip to Song
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ADVANCED MODE - Step 2: Song Generation */}
        {!isQuickMode && step === 2 && (
          <div className="space-y-6" data-testid="step-2-song">
            {/* Editable Lyrics Section */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    <span>{lyrics ? 'Edit Lyrics' : 'Add Lyrics'}</span>
                    <CoachEye targetId="create_adv.lyrics" label="Add / Edit Lyrics" />
                  </h3>
                  {lyrics && (
                    <span className="text-xs text-zinc-500">(Optional - leave empty for instrumental)</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openPromptHelper('lyrics')}
                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                    data-testid="lyrics-structure-helper-btn"
                  >
                    <HelpCircle className="h-3.5 w-3.5" />
                    Structure Tags
                  </button>
                  <CoachEye targetId="create_adv.structure_tags" label="Structure Tags" />
                  {lyrics && (
                    <Button
                      onClick={handleCopyLyrics}
                      variant="ghost"
                      size="sm"
                      className="text-zinc-400 hover:text-white h-7 px-2"
                    >
                      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                </div>
              </div>
              <textarea
                ref={lyricsRef}
                value={lyrics}
                onChange={(e) => {
                  setLyrics(e.target.value);
                  if (lyricsFromAI) setLyricsFromAI(false);  // user edited — badge no longer accurate
                }}
                onFocus={() => setLastFocusedField('lyrics')}
                onSelect={(e) => setCursorPosition(e.target.selectionStart)}
                onClick={(e) => setCursorPosition(e.target.selectionStart)}
                onKeyUp={(e) => setCursorPosition(e.target.selectionStart)}
                placeholder="Enter or paste your lyrics here... Use structure tags like [Verse], [Chorus], [Bridge] to organize sections. Leave empty for instrumental."
                className="textarea-dark w-full h-48 font-mono text-sm"
                data-testid="lyrics-editor"
                maxLength={5000}
              />
              <div className="flex items-center justify-between mt-2">
                <span className={`text-xs ${lyrics.length > 4500 ? (lyrics.length > 5000 ? 'text-red-400' : 'text-yellow-400') : 'text-zinc-500'}`}>
                  {lyrics.length.toLocaleString()}/5,000 characters
                </span>
                {lyricsFromAI && (
                  <span
                    className="text-xs text-purple-300/90 bg-purple-500/10 border border-purple-500/25 rounded-full px-2.5 py-0.5 font-medium"
                    data-testid="digital-musicians-badge"
                    title="These lyrics were written by our Digital Musicians using the theme, mood, and instructions you provided."
                  >
                    ✨ Your lyrics provided by our Digital Musicians guided by your instructions
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between mt-3">
                <Button
                  onClick={() => setStep(1)}
                  variant="ghost"
                  size="sm"
                  className="text-zinc-400 hover:text-white"
                >
                  ← Regenerate Lyrics
                </Button>
                <p className="text-xs text-zinc-500">
                  Tip: Click "Structure Tags" to add song sections
                </p>
              </div>
            </div>

            {/* Song Generation Form */}
            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Music className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Song Generation</h2>
                  <p className="text-sm text-zinc-400">FREE • Powered by SUNO AI</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="songTitle" className="text-white">Song Title (Optional)</Label>
                    <span className={`text-xs ${songTitle.length > 80 ? 'text-red-400' : 'text-zinc-500'}`}>
                      {songTitle.length}/80
                    </span>
                  </div>
                  <Input
                    id="songTitle"
                    value={songTitle}
                    onChange={(e) => setSongTitle(e.target.value.slice(0, 80))}
                    placeholder="Give your song a name..."
                    className="input-dark"
                    data-testid="song-title-input"
                    maxLength={80}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="songPrompt" className="text-white flex items-center gap-2">
                      <span>Style Builder</span>
                      <CoachEye targetId="create_adv.style_prompt_tags" label="Style Builder & Prompt Tags" />
                    </Label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setStylePresetPickerOpen(true)}
                        className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                        data-testid="open-style-preset-picker-adv-step2"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        Style Inspired By…
                      </button>
                      <CoachEye targetId="create.style_inspired_by" label="Style Inspired By" />
                      <button
                        type="button"
                        onClick={() => openPromptHelper('style')}
                        className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                        data-testid="style-helper-btn"
                      >
                        <HelpCircle className="h-3.5 w-3.5" />
                        Prompt Tags
                      </button>
                      <CoachEye targetId="create.suno_prompt_tags_ref" label="SUNO Prompt Tags Reference" />
                    </div>
                  </div>
                  <textarea
                    ref={styleRef}
                    id="songPrompt"
                    value={songPrompt}
                    onChange={(e) => setSongPrompt(e.target.value)}
                    onFocus={() => setLastFocusedField('style')}
                    placeholder="Your style tags will appear here..."
                    className="textarea-dark w-full h-20"
                    data-testid="song-prompt-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Add style tags or use Prompt Tags for presets.
                  </p>
                  <p className="text-[11px] text-amber-400/90 mt-1.5 leading-snug" data-testid="song-prompt-artist-warning">
                    {sunoModel === 'MUSICGPT' ? (
                      <>
                        <span className="font-semibold">Tip:</span> specific artist or band styles can be selected from
                        our <span className="text-primary font-medium">Prompt Tags</span> presets, or you can request
                        &ldquo;in the style of [Artist/Band Name]&rdquo; directly.
                      </>
                    ) : (
                      <>
                        <span className="font-semibold">Heads up:</span> don&apos;t type real artist names here.
                        Use the <span className="text-primary font-medium">Prompt Tags</span> button above for genre/style presets.
                      </>
                    )}
                  </p>
                </div>

                {/* Mood & Genre Selection - insert as tags */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="songMood" className="text-white mb-2 block">Mood (Optional)</Label>
                    <select
                      id="songMood"
                      value={songMood}
                      onChange={(e) => {
                        const newMood = e.target.value;
                        // Remove old mood tag if exists
                        if (songMood) {
                          setSongPrompt(prev => prev.replace(`[${songMood}]`, '').replace(/\s+/g, ' ').trim());
                        }
                        setSongMood(newMood);
                        // Insert new mood tag
                        if (newMood) {
                          const newTag = `[${newMood}]`;
                          setSongPrompt(prev => {
                            if (prev.includes(newTag)) {
                              return prev;
                            }
                            return prev ? `${newTag} ${prev}` : newTag;
                          });
                        }
                      }}
                      className="custom-select w-full"
                      data-testid="song-mood-select"
                    >
                      <option value="">-- None --</option>
                      {moods.map((m) => (
                        <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="songGenre" className="text-white mb-2 block">Genre (Optional)</Label>
                    <select
                      id="songGenre"
                      value={songGenre}
                      onChange={(e) => {
                        const newGenre = e.target.value;
                        // Remove old genre tag if exists
                        if (songGenre) {
                          setSongPrompt(prev => prev.replace(`[${songGenre}]`, '').replace(/\s+/g, ' ').trim());
                        }
                        setSongGenre(newGenre);
                        // Insert new genre tag
                        if (newGenre) {
                          const newTag = `[${newGenre}]`;
                          setSongPrompt(prev => {
                            if (prev.includes(newTag)) {
                              return prev;
                            }
                            return prev ? `${newTag} ${prev}` : newTag;
                          });
                        }
                      }}
                      className="custom-select w-full"
                      data-testid="song-genre-select"
                    >
                      <option value="">-- None --</option>
                      {genres.map((g) => (
                        <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Voice Selection — always visible (user request).
                    When instrumental is on, buttons are visually
                    disabled but remain in place. */}
                <div>
                  <Label className="text-white mb-2 block">Voice</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {voices.map((v) => {
                      const isActive = voice === v.id && !instrumental;
                      return (
                        <button
                          key={v.id || 'none'}
                          type="button"
                          disabled={instrumental}
                          onClick={() => setVoice(v.id)}
                          className={`p-3 rounded-lg text-center transition-all ${
                            isActive
                              ? 'bg-primary/20 border-2 border-primary'
                              : 'bg-black/20 border-2 border-white/10 hover:border-white/20'
                          } ${instrumental ? 'opacity-40 cursor-not-allowed' : ''}`}
                          data-testid={`voice-${v.id || 'none'}`}
                        >
                          <div className="text-xl mb-1">{v.emoji}</div>
                          <div className="font-medium text-white text-sm">{v.label}</div>
                        </button>
                      );
                    })}
                  </div>
                  {instrumental && (
                    <p className="text-xs text-zinc-500 mt-2">
                      🎹 Instrumental mode — voice selection disabled
                    </p>
                  )}
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  <p className="text-sm text-yellow-500">
                    <strong>Note:</strong> Song generation takes {sunoModel === 'MUSICGPT' ? '1-2' : '2-4'} minutes. You'll be redirected to the song page where you can track the progress.
                  </p>
                </div>

                <Button
                  onClick={handleGenerateSong}
                  disabled={isGeneratingSong || coverTranscribing || (coverMode ? !coverAudioUrl : (!songPrompt.trim() && !lyrics.trim()))}
                  className="btn-primary w-full"
                  data-testid="generate-song-btn"
                >
                  {coverTranscribing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Transcribing lyrics…
                    </>
                  ) : isGeneratingSong ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Starting Generation...
                    </>
                  ) : (
                    <>
                      🎵 Create My Song
                      <span className="ml-2 text-sm opacity-80">
                        • {renderCost} credit{renderCost === 1 ? '' : 's'}
                      </span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
          </div>

          {/* Right Sidebar - Recent Songs - Dynamic height */}
          <SongsSidebar maxHeight={sidebarHeight} />
        </div>
      </main>

      {/* Prompt Helper Dialog */}
      <Dialog open={showPromptHelper} onOpenChange={setShowPromptHelper}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              <span>SUNO Prompt Tags Reference</span>
              <CoachEye targetId="create.suno_prompt_tags_ref" label="SUNO Prompt Tags Reference" />
              <CoachEye targetId="create.prompt_tags.howto" label="How to use Prompt Tags" />
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-sm text-zinc-400 mb-4">
            Click on any tag to add it to your {promptHelperTarget === 'lyrics' ? 'lyrics' : promptHelperTarget === 'style' ? 'style description' : 'additional context'}.
          </p>

          {/* Search */}
          <div className="mb-4">
            <Input
              placeholder="Search tags..."
              value={tagSearchQuery}
              onChange={(e) => setTagSearchQuery(e.target.value)}
              className="input-dark"
            />
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
            {[
              { id: 'examples', label: '📖 Examples', color: 'purple-500' },
              { id: 'artists', label: '🎨 Style Presets', color: 'purple-400' },
              { id: 'structure', label: '🏗️ Structure', color: 'primary' },
              { id: 'vocals', label: '🎤 Vocals', color: 'pink-500' },
              { id: 'instruments', label: '🎹 Instruments', color: 'blue-500' },
              { id: 'mood', label: '💫 Mood', color: 'yellow-500' },
              { id: 'genres', label: '🎵 Genres', color: 'green-500' },
              { id: 'effects', label: '🔊 Effects', color: 'orange-500' }
            ].map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveHelperCategory(cat.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                  activeHelperCategory === cat.id
                    ? 'bg-primary text-black'
                    : 'bg-white/10 text-zinc-400 hover:bg-white/20'
                }`}
              >
                {cat.id === 'examples' ? cat.label : `${cat.label} (${allTags.filter(t => t.category === cat.id).length})`}
              </button>
            ))}
          </div>

          {/* Usage Examples Section */}
          {activeHelperCategory === 'examples' && (
            <div className="space-y-4">
              {/* Structure Example */}
              <div className="bg-black/30 rounded-lg p-4">
                <h4 className="text-primary font-semibold mb-2 flex items-center gap-2">
                  <span>🏗️</span> Structure Implementation
                </h4>
                <pre className="text-xs text-zinc-300 bg-black/50 p-3 rounded overflow-x-auto whitespace-pre-wrap font-mono">
{`[Intro]
(Ambient strings)

[Verse 1]
The forest path winds down the hill
towards the light

[Pre-Chorus]
I can see it in your eyes

[Chorus]
Call them once, Call them twice
One more call to make it thrice

[Break]
(Stripped drums and percussion)

[Verse 2]
The dogs all run so strong and free
They hear my call they come to me`}
                </pre>
              </div>

              {/* Voice Tags Example */}
              <div className="bg-black/30 rounded-lg p-4">
                <h4 className="text-pink-400 font-semibold mb-2 flex items-center gap-2">
                  <span>🎤</span> Suno Voice Tags Example
                </h4>
                <pre className="text-xs text-zinc-300 bg-black/50 p-3 rounded overflow-x-auto whitespace-pre-wrap font-mono">
{`[Verse 1]
[Female Vocal] [Soft] [Whisper]
I love you like no other could

[Chorus]
[Female Vocal] [Powerful] [Belting]
My love will last forever and a day`}
                </pre>
              </div>

              {/* Combined Mood/Energy Example */}
              <div className="bg-black/30 rounded-lg p-4">
                <h4 className="text-yellow-400 font-semibold mb-2 flex items-center gap-2">
                  <span>💫</span> Combined Mood/Energy Example
                </h4>
                <p className="text-xs text-zinc-400 mb-2">Style prompt:</p>
                <pre className="text-xs text-zinc-300 bg-black/50 p-3 rounded overflow-x-auto whitespace-pre-wrap font-mono">
{`[Mood: Nostalgic] [Energy: Medium] [Texture: Lo-fi] [Warm] [Dreamy]

[Verse]
[Soft] [Intimate]
Ocean waves upon the shore 
Sing softly as they tell the lore`}
                </pre>
              </div>

              {/* Sound Effects Example */}
              <div className="bg-black/30 rounded-lg p-4">
                <h4 className="text-orange-400 font-semibold mb-2 flex items-center gap-2">
                  <span>🔊</span> Sound Effects Example
                </h4>
                <pre className="text-xs text-zinc-300 bg-black/50 p-3 rounded overflow-x-auto whitespace-pre-wrap font-mono">
{`[Intro]
[Rain] [Thunder]
(Distant voices getting closer and growing louder)

[Verse]
[Whispers] The eye of the storm is passing
[Soft] and the thunder is in the distance getting closer again

[Chorus]
[Powerful] [Cheering]
But together we will hold on tight`}
                </pre>
              </div>

              <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-sm text-primary">
                  <strong>💡 Pro Tips:</strong>
                </p>
                <ul className="text-xs text-zinc-300 mt-2 space-y-1 list-disc list-inside">
                  <li>Use parentheses <code className="bg-black/30 px-1 rounded">(like this)</code> for instrumental/production notes</li>
                  <li>Combine multiple tags on a single line for layered effects</li>
                  <li>Place mood/energy tags in your style prompt for overall feel</li>
                  <li>Use voice tags before lyrics to control delivery style</li>
                </ul>
              </div>
            </div>
          )}

          {/* Artists Section - Simple table format like other tags */}
          {activeHelperCategory === 'artists' && (
            <>
              <p className="text-sm text-zinc-400 mb-3">
                Style presets inspired by various musical influences. Click to preview, then add to your prompt.
              </p>
              
              {/* Selected Style Preview - shows the actual prompts with Add button */}
              {selectedArtist && (
                <div className="mb-4 p-4 bg-primary/10 border border-primary/30 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-white font-medium">Preview: {selectedArtist.tag}</span>
                    <button 
                      type="button"
                      onClick={() => setSelectedArtist(null)}
                      className="text-zinc-400 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Show the prompts that will be added */}
                  <p className="text-xs text-zinc-400 mb-2">These tags will be added to your prompt:</p>
                  {selectedArtist.style_prompts && selectedArtist.style_prompts.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {selectedArtist.style_prompts.map((prompt, idx) => (
                        <span key={`${prompt}-${idx}`} className="px-2 py-1 bg-primary/20 text-primary text-xs rounded font-mono">
                          [{prompt}]
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Add Button */}
                  <Button
                    onClick={confirmAddArtistPrompts}
                    className="w-full bg-primary hover:bg-primary/90 text-black font-medium"
                  >
                    Add to {promptHelperTarget === 'style' ? 'Style Builder' : 'Style Builder'}
                  </Button>
                </div>
              )}

              {/* Artists Table - Grouped by subcategory */}
              <div className="bg-black/30 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                {(() => {
                  // Group presets by subcategory
                  const filteredPresets = allTags
                    .filter(t => t.category === 'artists')
                    .filter(t => !tagSearchQuery || 
                      t.tag.toLowerCase().includes(tagSearchQuery.toLowerCase()) ||
                      (t.description && t.description.toLowerCase().includes(tagSearchQuery.toLowerCase())) ||
                      (t.subcategory && t.subcategory.toLowerCase().includes(tagSearchQuery.toLowerCase()))
                    );
                  
                  const grouped = {};
                  filteredPresets.forEach(preset => {
                    const sub = preset.subcategory && preset.subcategory !== 'None' ? preset.subcategory : 'General';
                    if (!grouped[sub]) grouped[sub] = [];
                    grouped[sub].push(preset);
                  });
                  
                  // Sort subcategories, with "General" last
                  const sortedSubs = Object.keys(grouped).sort((a, b) => {
                    if (a === 'General') return 1;
                    if (b === 'General') return -1;
                    return a.localeCompare(b);
                  });
                  
                  // Sort presets within each subcategory
                  sortedSubs.forEach(sub => {
                    grouped[sub].sort((a, b) => a.tag.localeCompare(b.tag));
                  });
                  
                  return sortedSubs.map(subcategory => (
                    <div key={subcategory}>
                      <div className="sticky top-0 bg-zinc-900/95 px-3 py-2 border-b border-white/10 z-10">
                        <span className="text-xs font-semibold text-purple-400 uppercase tracking-wide">{subcategory}</span>
                        <span className="text-xs text-zinc-500 ml-2">({grouped[subcategory].length})</span>
                      </div>
                      <table className="w-full text-sm">
                        <tbody>
                          {grouped[subcategory].map((artist) => (
                            <tr 
                              key={artist.tag_id || artist.tag} 
                              className={`border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${
                                selectedArtist?.tag_id === artist.tag_id ? 'bg-primary/10' : ''
                              }`}
                              onClick={() => selectArtist(artist)}
                            >
                              <td className="py-2 px-3 w-48">
                                <span className="text-zinc-300 text-xs">
                                  {artist.tag}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-zinc-400 text-xs">{artist.description || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ));
                })()}
                {allTags.filter(t => t.category === 'artists').length === 0 && (
                  <div className="text-center py-4 text-zinc-400 text-sm">No style presets available. Import them from the Admin Panel.</div>
                )}
              </div>
              
              {/* Show count of filtered results */}
              <div className="mt-2 text-xs text-zinc-500">
                Showing {allTags.filter(t => t.category === 'artists').filter(t => !tagSearchQuery || t.tag.toLowerCase().includes(tagSearchQuery.toLowerCase()) || (t.description && t.description.toLowerCase().includes(tagSearchQuery.toLowerCase()))).length} of {allTags.filter(t => t.category === 'artists').length} presets
              </div>

              <div className="mt-4 p-3 bg-zinc-800/30 border border-white/10 rounded-lg">
                <p className="text-sm text-zinc-400">
                  <strong>Note:</strong> These presets add genre, instrument & production style tags. 
                  All vocals are AI-generated.
                </p>
              </div>
            </>
          )}

          {/* Tags Table - for non-examples, non-artists tabs */}
          {activeHelperCategory !== 'examples' && activeHelperCategory !== 'artists' && (
            <>
              <div className="bg-black/30 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                {(() => {
                  // Group tags by subcategory
                  const filteredTags = allTags
                    .filter(t => t.category === activeHelperCategory)
                    .filter(t => !tagSearchQuery || 
                      t.tag.toLowerCase().includes(tagSearchQuery.toLowerCase()) ||
                      (t.description && t.description.toLowerCase().includes(tagSearchQuery.toLowerCase()))
                    );
                  
                  const grouped = {};
                  filteredTags.forEach(tag => {
                    const sub = tag.subcategory && tag.subcategory !== 'None' ? tag.subcategory : 'General';
                    if (!grouped[sub]) grouped[sub] = [];
                    grouped[sub].push(tag);
                  });
                  
                  // Sort subcategories, with "General" last
                  const sortedSubs = Object.keys(grouped).sort((a, b) => {
                    if (a === 'General') return 1;
                    if (b === 'General') return -1;
                    return a.localeCompare(b);
                  });
                  
                  // Sort tags within each subcategory
                  sortedSubs.forEach(sub => {
                    grouped[sub].sort((a, b) => a.tag.localeCompare(b.tag));
                  });
                  
                  return sortedSubs.map(subcategory => (
                    <div key={subcategory}>
                      <div className="sticky top-0 bg-zinc-900/95 px-3 py-2 border-b border-white/10">
                        <span className="text-xs font-semibold text-primary uppercase tracking-wide">{subcategory}</span>
                        <span className="text-xs text-zinc-500 ml-2">({grouped[subcategory].length})</span>
                      </div>
                      <table className="w-full text-sm">
                        <tbody>
                          {grouped[subcategory].map((item) => (
                            <tr 
                              key={item.tag_id || item.tag} 
                              className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                              onClick={() => insertTag(item.tag, activeHelperCategory)}
                            >
                              <td className="py-2 px-3 w-36">
                                <code className="bg-primary/20 text-primary px-2 py-0.5 rounded text-xs font-mono">
                                  {item.tag}
                                </code>
                              </td>
                              <td className="py-2 px-3 text-zinc-300 text-xs">{item.description}</td>
                              <td className="py-2 px-3 text-zinc-400 text-xs w-40">{item.use_case}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ));
                })()}
                {allTags.filter(t => t.category === activeHelperCategory).length === 0 && (
                  <div className="text-center py-4 text-zinc-400 text-sm">No tags available in this category</div>
                )}
              </div>

              <div className="mt-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-sm text-primary">
                  <strong>Tip:</strong> Use multiple tags to customize your song. 
                  Example: <code className="bg-black/30 px-1 rounded">[Verse] [Female Vocal] [Piano] [Melancholic]</code>
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* v2.9.35 — Style preset picker modal */}
      <StylePresetPicker
        isOpen={stylePresetPickerOpen}
        onClose={() => setStylePresetPickerOpen(false)}
        onApply={applyStylePreset}
        engineLabel={sunoModel}
      />
    </div>
  );
}
